import { Injectable } from '@angular/core';
import { UserRoles } from '../dictionaries/UserRoles';
import { ShareFilesDialogResult } from '../models/ShareFilesDialogResult';
import { FirebaseUtilitiesService } from './firebase-utilities.service';
import { UtilsService } from './utils.service';
import { UIMessagingService } from './uimessaging.service';

/**
 * Service for handling file sharing business logic in ClientProfileComponent
 * Extracted to reduce component complexity and improve maintainability
 */
@Injectable({
  providedIn: 'root'
})
export class ClientProfileSharingService {
  constructor(
    private firebaseUtilitiesService: FirebaseUtilitiesService,
    private utilsService: UtilsService,
    private uiMessagingService: UIMessagingService
  ) {}

  /**
   * Handles sharing files with a consultant
   * @param shareObject - Sharing object with patient, user, and files
   * @param casename - Case name
   * @param userEmail - Current user email
   * @returns Promise that resolves when sharing is complete
   */
  async handleShareWithConsultant(
    shareObject: ShareFilesDialogResult,
    casename: string,
    userEmail: string
  ): Promise<void> {
    const { patient, user, files } = shareObject;
    const folderId = `d_${patient?.caseName}_${user?.id}_${this.utilsService.getRandomString_(10)}`;

    // Create consultant folder
    await this.firebaseUtilitiesService.createFolder({
      folderId: folderId,
      type: 'folder',
      children: '',
      parentFolder: '',
      belongsTo: patient?.caseName,
      forDelete: false,
      predefined: false,
      color: '#000000',
      restrictedTo: [user?.email],
      name: user?.name
    });

    // Clone files for consultant
    const clonedFiles = files.map(file => ({
      ...file,
      readOnly: true,
      parentFolder: folderId,
      parentFolderName: user?.name,
      id: 'cloned_' + file.id,
      fileId: 'cloned_' + file.fileId
    }));

    await this.firebaseUtilitiesService.createClonedFiles(clonedFiles);

    // Share files with consultant
    await Promise.all(
      files.map(file =>
        this.firebaseUtilitiesService.shareFileV2(
          { fileName: file.filename, fileId: file.fileId },
          {
            name: user?.name,
            email: user?.email,
            role: user?.role,
            uid: user?.uid,
            id: user?.id
          },
          patient?.caseName,
          userEmail
        )
      )
    );

    // Update patient shared with consultant
    await this.firebaseUtilitiesService.updatePatientSharedWithConsultant(patient?.caseName, user?.email);
  }

  /**
   * Handles sharing files with facility facilitators
   * @param shareObject - Sharing object with patient, facility, user, and files
   * @param userOwnerID - User owner ID
   * @param userEmail - Current user email
   * @param userFacility - Current user facility
   * @returns Promise that resolves when sharing is complete
   */
  async handleShareWithFacilityFacilitators(
    shareObject: ShareFilesDialogResult,
    userOwnerID: string,
    userEmail: string,
    userFacility: string
  ): Promise<void> {
    // Get the facilitators for the facility
    const facilitators = await this.firebaseUtilitiesService.getFacilitatorsByFacilityId(
      shareObject.facility?.id
    );
    const facilitatorsEmails = facilitators.map(({ email }) => email);

    if (!facilitatorsEmails.length) {
      this.uiMessagingService.toastMessage(
        'No facilitators found in the facility. Please add at least one facilitator before sharing files.',
        'WARNING',
        { duration: 5000 }
      );
      return;
    }

    // Get or create facility folder
    let folderId: string | undefined;
    const facilityFolder = await this.firebaseUtilitiesService.getFolderByFacilityId(shareObject.facility?.id);

    if (!facilityFolder) {
      // Create the facility folder
      folderId = `d_${shareObject.patient?.caseName}_${shareObject.facility?.id}_${this.utilsService.getRandomString_(10)}`;

      await this.firebaseUtilitiesService.createFolder({
        folderId: folderId,
        type: 'folder',
        children: '',
        parentFolder: '',
        facilityId: shareObject.facility?.id,
        name: shareObject.facility?.name,
        belongsTo: shareObject.patient?.caseName,
        ownerID: userOwnerID,
        forDelete: false,
        predefined: false,
        shared_files: true,
        restrictedTo: facilitatorsEmails,
        color: '#000000'
      });
    } else {
      folderId = facilityFolder?.folderId;
    }

    // Locate the "Shared Files" folder
    const sharedFilesFolder = await this.firebaseUtilitiesService.getSharedFilesFolderByFacilityId(
      shareObject.facility?.id
    );

    if (!sharedFilesFolder) {
      this.uiMessagingService.toastMessage(
        'No shared files folder found in the facility. Please create a shared files folder before sharing files.',
        'WARNING',
        { duration: 5000 }
      );
      return;
    }

    folderId = sharedFilesFolder?.folderId;
    if (!folderId) {
      this.uiMessagingService.toastMessage(
        'No shared files folder found in the facility. Please create a shared files folder before sharing files.',
        'WARNING',
        { duration: 5000 }
      );
      return;
    }

    const facilityOwnerID = await this.firebaseUtilitiesService.getFacilityOwnerID(shareObject.facility?.id);
    // Normalize to lowercase-hyphen (matches updatePatientSharedWithFacility convention)
    const targetFacility = (shareObject.facility?.id ?? '').toLowerCase().replace(/_/g, '-');

    // Clone and share files.
    // sharedWithFacilityMap is written at creation time so the Firestore allow list rule
    // (which cannot call get()) can authorize facility user queries on the files collection.
    const clonedFiles = shareObject.files.map(file => ({
      ...file,
      readOnly: true,
      parentFolder: sharedFilesFolder?.folderId,
      parentFolderName: sharedFilesFolder?.name,
      id: 'cloned_' + file.id,
      fileId: 'cloned_' + file.fileId,
      ownerID: facilityOwnerID,
      sharedWithFacilityMap: { [targetFacility]: true }
    }));

    await this.firebaseUtilitiesService.createClonedFiles(clonedFiles);

    // Update the patient sharedWith field to include the new facility
    await this.firebaseUtilitiesService.updatePatientSharedWithFacility(
      shareObject.patient?.caseName,
      shareObject.facility?.id,
      userFacility
    );
  }

  /**
   * Sends email notification for shared files
   * @param targetEmail - Target email address
   * @param extraMessage - Extra message (optional)
   * @param files - Array of files
   * @param casename - Case name
   */
  sendNotification(
    targetEmail: string,
    extraMessage: string | null,
    files: any[],
    casename: string
  ): void {
    this.firebaseUtilitiesService.sendEmailNotification(
      {
        targetEmail: targetEmail.toLowerCase(),
        caseName: casename.toLowerCase(),
        files: files.map(file => file.filename.toLowerCase()).join(', '),
        extraMessage: extraMessage?.toLowerCase()
      },
      'share'
    );
  }

  /**
   * Determines if sharing should be with consultant or facility facilitators
   * @param shareObject - Sharing object
   * @returns True if sharing with facility facilitators (admin), false if consultant
   */
  shouldShareWithFacilityFacilitators(shareObject: ShareFilesDialogResult): boolean {
    return shareObject.user?.role === UserRoles.admin;
  }
}
